import { useEffect, useMemo, useState } from "react";
import axios from "../../services/axios";
import {
  Salter,
  SalterPayload,
  salterService,
} from "../../services/salterService";
import { useEditOnboarding } from "../../hooks/useEditOnboarding";
import { useFilter } from "../../hooks/useFilter";
import {
  DropdownV2,
  FormCardV2,
  FormGridV2,
  MultiDropdownV2,
  OptionV2,
} from "../FormControlsV2";
import { FooterEditOnboarding } from "../EditOnboarding/Footer";
import "./styles.css";

type FarmOption = OptionV2<number> & { info: any };

export function SalterFormV2() {
  const { defaultValue, setOptionForm } = useEditOnboarding();
  const { responseFarms } = useFilter();
  const salter = defaultValue as Salter;
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [areas, setAreas] = useState<OptionV2<number>[]>([]);
  const [selectedFarms, setSelectedFarms] = useState<OptionV2<number>[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<OptionV2<number>[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const farmOptions: FarmOption[] = (responseFarms || []).map((farm) => ({
      label: farm.name,
      value: Number(farm.id),
      info: farm,
    }));
    setFarms(farmOptions);
    Promise.all(
      farmOptions.map((farm) =>
        axios
          .get("/areas", { params: { farm_id: farm.value } })
          .then(({ data }) =>
            data.map((area: any) => ({
              label: area.name,
              value: Number(area.id),
              info: { ...area, farm_id: Number(farm.value) },
            })),
          ),
      ),
    ).then((groups) => setAreas(groups.flat()));
  }, [responseFarms]);

  useEffect(() => {
    setSelectedFarms(
      farms.filter((farm) =>
        salter?.farms?.some((item) => Number(item.id) === Number(farm.value)),
      ),
    );
  }, [farms, salter]);

  useEffect(() => {
    setSelectedAreas(
      areas.filter((area) =>
        salter?.areas?.some((item) => Number(item.id) === Number(area.value)),
      ),
    );
  }, [areas, salter]);

  const userOption = useMemo(
    () =>
      salter?.user ? { label: salter.user.name, value: salter.user.id } : null,
    [salter],
  );

  function changeFarms(next: OptionV2<number>[]) {
    const addedIds = next
      .filter((farm) => !selectedFarms.some((old) => old.value === farm.value))
      .map((farm) => Number(farm.value));
    const removedIds = selectedFarms
      .filter((farm) => !next.some((item) => item.value === farm.value))
      .map((farm) => Number(farm.value));
    setSelectedFarms(next);
    setSelectedAreas((current) => {
      const withoutRemoved = current.filter(
        (area) => !removedIds.includes(Number(area.info?.farm_id)),
      );
      const additions = areas.filter((area) =>
        addedIds.includes(Number(area.info?.farm_id)),
      );
      return [
        ...withoutRemoved,
        ...additions.filter(
          (area) => !withoutRemoved.some((item) => item.value === area.value),
        ),
      ];
    });
  }

  function changeAreas(next: OptionV2<number>[]) {
    setSelectedAreas(next);
    setSelectedFarms((current) =>
      current.filter((farm) => {
        const farmAreas = areas.filter(
          (area) => Number(area.info?.farm_id) === Number(farm.value),
        );
        return (
          farmAreas.length === 0 ||
          farmAreas.every((area) =>
            next.some((item) => item.value === area.value),
          )
        );
      }),
    );
  }

  function submit() {
    if (!selectedFarms.length && !selectedAreas.length) {
      setError("Selecione pelo menos uma fazenda ou um retiro.");
      return;
    }
    const payload: SalterPayload = {
      user_id: Number(salter.user.id),
      farm_ids: selectedFarms.map((item) => Number(item.value)),
      area_ids: selectedAreas.map((item) => Number(item.value)),
    };
    setSaving(true);
    salterService
      .save(payload, salter.id)
      .then(() => setOptionForm(undefined))
      .catch((reason) =>
        setError(
          reason?.response?.data?.msg || "Não foi possível salvar o salgador.",
        ),
      )
      .finally(() => setSaving(false));
  }

  return (
    <div className="salter-form">
      <div className="salter-form__content">
        <FormCardV2>
          <FormGridV2>
            <DropdownV2
              name="salter-user"
              label="Usuário"
              value={userOption}
              options={userOption ? [userOption] : []}
              disabled
            />
            <MultiDropdownV2
              name="salter-farms"
              label="Fazendas"
              description="Selecionar uma fazenda também seleciona todos os retiros vinculados a ela."
              value={selectedFarms}
              options={farms}
              searchable
              onChange={changeFarms}
            />
            <MultiDropdownV2
              name="salter-areas"
              label="Retiros"
              value={selectedAreas}
              options={areas}
              searchable
              onChange={changeAreas}
            />
          </FormGridV2>
          {error && <small className="ov2-field__error">{error}</small>}
        </FormCardV2>
      </div>
      <FooterEditOnboarding
        isNew={!salter?.id}
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submit}
        disabledSubmit={saving}
      />
    </div>
  );
}
